import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { NPC_COMPLIANCE } from '../lib/privacy';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <img
                src="/Agos_logo.png"
                alt="Agos Logo"
                className="h-12 w-12 object-contain hover:scale-110 transition-transform"
              />
              <div className="absolute inset-0 bg-blue-500/20 blur-lg hover:bg-blue-400/30 transition-all" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
          <h2 className="text-xl text-gray-400 mt-2">Patakaran sa Pagkapribado</h2>
          <p className="mt-4 text-gray-300">Last updated: January 22, 2026</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="prose prose-invert max-w-none space-y-6"
        >
          {/* NPC Compliance Notice */}
          <div className="glass-card p-6 rounded-lg border border-blue-500/30 bg-blue-900/20">
            <h2 className="text-xl font-bold text-blue-400 mb-4">
              Philippine Data Privacy Act Compliance
            </h2>
            <p className="text-gray-300 text-sm mb-4">
              Agos Identity Verification is committed to complying with Republic Act No. 10173,
              also known as the Data Privacy Act of 2012 (DPA), and its Implementing Rules and
              Regulations. NPC registration is currently in progress. Agos is designed for 
              full NPC compliance with production deployment (Q2 2026) using LightNode Manila 
              localized data residency.
            </p>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Data Protection Officer:</p>
                <p className="text-white">{NPC_COMPLIANCE.dpo.name || 'To be appointed'}</p>
                <p className="text-blue-400">{NPC_COMPLIANCE.dpo.email}</p>
              </div>
              <div>
                <p className="text-gray-400">NPC Registration:</p>
                <p className="text-white">{NPC_COMPLIANCE.registrationNumber || 'Registration in progress'}</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-8 rounded-lg">
            <h2 className="text-2xl font-bold text-white mb-6">1. Information We Collect</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">1. Impormasyon na Kinokolekta Namin</h3>
            <p className="text-gray-300 mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
              <li><strong>Account Information:</strong> Email address, password, company name, and contact details</li>
              <li><strong>Identity Documents:</strong> Government-issued IDs (PhilID, UMID, Driver's License, Passport, SSS, TIN, PhilHealth, etc.) for verification purposes</li>
              <li><strong>Biometric Data:</strong> Facial images for identity verification and liveness detection</li>
              <li><strong>Verification Data:</strong> Secure cryptographic hashes of ID identifiers (we do not store your actual ID number in plaintext)</li>
              <li><strong>Transaction Information:</strong> Payment details, verification history</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-6">2. How We Use Your Information</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">2. Paano Namin Ginagamit ang Iyong Impormasyon</h3>
            <p className="text-gray-300 mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
              <li>Provide identity verification services using privacy-preserving technology</li>
              <li>Process your transactions and payments</li>
              <li>Comply with Anti-Money Laundering Act (AMLA) requirements</li>
              <li>Improve our services and develop new features</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and requests</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-6">3. Legal Basis for Processing (DPA Section 12)</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">3. Legal na Batayan para sa Pagproseso</h3>
            <p className="text-gray-300 mb-4">
              We process your personal information based on the following legal grounds:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
              <li><strong>Consent:</strong> You have given your explicit consent for processing</li>
              <li><strong>Contract:</strong> Processing is necessary to fulfill our service agreement with you</li>
              <li><strong>Legal Obligation:</strong> Processing is required to comply with AMLA, BSP regulations, and other applicable laws</li>
              <li><strong>Legitimate Interest:</strong> Processing is necessary for our legitimate business interests, balanced against your rights</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-6">4. Data Retention</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">4. Pagpapanatili ng Data</h3>
            <p className="text-gray-300 mb-4">
              In compliance with AMLA and NPC guidelines, we retain your data for the following periods:
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 text-gray-400">Data Type</th>
                    <th className="text-left py-2 text-gray-400">Retention Period</th>
                    <th className="text-left py-2 text-gray-400">Legal Basis</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Verification Records</td>
                    <td className="py-2">5 years</td>
                    <td className="py-2">AMLA Requirement</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Payment Data</td>
                    <td className="py-2">5 years</td>
                    <td className="py-2">AMLA + BIR</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Biometric Image / ID Image</td>
                    <td className="py-2">24 hours</td>
                    <td className="py-2">Privacy Optimization / NPC</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Biometric Score/Result</td>
                    <td className="py-2">5 years</td>
                    <td className="py-2">AMLA KYC</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Analytics Data</td>
                    <td className="py-2">1 year</td>
                    <td className="py-2">NPC Guideline</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2">Audit Logs</td>
                    <td className="py-2">1 year</td>
                    <td className="py-2">NPC Guideline</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-bold text-white mb-6">5. Data Security</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">5. Seguridad ng Data</h3>
            <p className="text-gray-300 mb-4">
              We implement robust technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
              <li>End-to-end encryption for all data transmission</li>
              <li>Privacy-preserving verification without exposing personal data</li>
              <li>Cryptographic immutable verification records</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and audit logging</li>
              <li>Secure data centers with physical security measures</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-6">6. Your Rights Under the DPA</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">6. Ang Iyong mga Karapatan sa ilalim ng DPA</h3>
            <p className="text-gray-300 mb-4">
              As a data subject under the Philippine Data Privacy Act, you have the following rights:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
              <li><strong>Right to be Informed:</strong> Know how your data is collected and processed</li>
              <li><strong>Right to Access:</strong> Obtain a copy of your personal data</li>
              <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Right to Data Portability:</strong> Receive your data in a commonly used format</li>
              <li><strong>Right to Damages:</strong> Claim compensation for damages due to data privacy violations</li>
              <li><strong>Right to File a Complaint:</strong> Lodge a complaint with the NPC</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mb-6">7. Cross-Border Data Transfer</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">7. Paglilipat ng Data sa Ibang Bansa</h3>
            <p className="text-gray-300 mb-6">
              Your data may be transferred to and processed in countries outside the Philippines 
              (such as Germany) for cloud storage and processing purposes during the initial 
              demonstration and pilot phase. We ensure that such transfers comply with NPC 
              Circular 2016-02. <strong>As we move toward final production and full NPC 
              registration, Agos Identity is committed to a "Data Localization" strategy, 
              ensuring that all sensitive personal information is stored and processed exclusively 
              within data centers located in the Philippines (e.g., Manila).</strong>
            </p>

            <h2 className="text-2xl font-bold text-white mb-6">8. Data Breach Notification</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">8. Abiso sa Paglabag ng Data</h3>
            <p className="text-gray-300 mb-6">
              In the event of a personal data breach that is likely to result in a risk to your
              rights and freedoms, we will notify the NPC within 72 hours of becoming aware of
              the breach, as required by NPC Circular 2016-03. Affected data subjects will be
              notified without undue delay. <strong>To mitigate the impact of any potential breach, 
              Agos Identity employs a data minimization strategy where sensitive identifiers (such 
              as ID numbers) are stored only as one-way cryptographic hashes, rendering them 
              unreadable and unusable even in the event of unauthorized access.</strong>
            </p>

            <h2 className="text-2xl font-bold text-white mb-6">9. Third-Party Sharing</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">9. Pagbabahagi sa Third Party</h3>
            <p className="text-gray-300 mb-4">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-gray-300 mb-6 space-y-2">
              <li>Payment processors (PayMongo, GCash, Maya) for transaction processing</li>
              <li>Cloud service providers for data storage and processing</li>
              <li>Government agencies when required by law (AMLC, BSP, NPC)</li>
              <li>Professional advisers (legal, audit, consulting)</li>
            </ul>
            <p className="text-gray-300 mb-6">
              All third-party processors are required to sign Data Processing Agreements (DPA)
              ensuring equivalent data protection standards.
            </p>

            <h2 className="text-2xl font-bold text-white mb-6">10. Contact Us</h2>
            <h3 className="text-lg font-semibold text-gray-200 mb-2">10. Makipag-ugnay sa Amin</h3>
            <p className="text-gray-300 mb-4">
              For questions about this Privacy Policy or to exercise your data subject rights,
              please contact our Data Protection Officer:
            </p>
            <div className="bg-gray-800 p-4 rounded-lg mb-6">
              <p className="text-white font-semibold">Data Protection Officer</p>
              <p className="text-gray-300">Agos Identity Verification</p>
              <p className="text-blue-400">{NPC_COMPLIANCE.dpo.email}</p>
              <p className="text-gray-300">{NPC_COMPLIANCE.dpo.phone}</p>
              <p className="text-gray-300">{NPC_COMPLIANCE.dpo.address}</p>
            </div>

            <p className="text-gray-300 mb-4">
              If you are not satisfied with our response, you may file a complaint with:
            </p>
            <div className="bg-gray-800 p-4 rounded-lg">
              <p className="text-white font-semibold">{NPC_COMPLIANCE.npcContact.name}</p>
              <p className="text-gray-300">{NPC_COMPLIANCE.npcContact.address}</p>
              <p className="text-gray-300">Phone: {NPC_COMPLIANCE.npcContact.phone}</p>
              <p className="text-blue-400">{NPC_COMPLIANCE.npcContact.email}</p>
              <a
                href={NPC_COMPLIANCE.npcContact.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-2"
              >
                {NPC_COMPLIANCE.npcContact.website}
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
